import { create } from 'zustand';
import dayjs from 'dayjs';
import { Shift, SHIFT_STATUSES, ShiftList } from '@libs/models/shifts/shift';
import { getAssignments, getEvents } from '@libs/api/shifts';
import { FiltersCollection, SortingModel } from '@libs/store/filters';
import { getAllTalents } from '@libs/api/talents';
import { StaffListItem } from '@libs/models/talents/talent';

export type Group = {
  groupId: string;
  groupName: string;
  items: {
    resourceInfo: StaffListItem;
    assignments: Shift[];
  }[];
};

type State = {
  loading: boolean;
  initiated: boolean;
  eventsList: ShiftList[];

  calendarDataLoading: boolean;

  assignmentsInitiated: boolean;
  assignmentsLoading: boolean;
  assignmentsList: { staffId: string; shiftId: string }[];

  resourcesInitiated: boolean;
  resourcesListLoading: boolean;
  resourcesList: StaffListItem[];

  entitiesGroups: Group[];
  unassignedShifts: Shift[];

  selectedShift?: Shift | null;
  setSelectedShift: (shift: Shift | null) => void;

  flushInitiated: () => void;
  loadCalendarData: (
    startDate: Date,
    endDate: Date,
    timezone: string,
    filters?: FiltersCollection,
    orderBy?: SortingModel,
  ) => Promise<void>;
};

export const useCalendarShiftsStore = create<State>()((set, get) => ({
  loading: false,
  initiated: false,
  eventsList: [],

  calendarDataLoading: true,

  assignmentsInitiated: false,
  assignmentsLoading: true,
  assignmentsList: [],

  resourcesInitiated: false,
  resourcesListLoading: true,
  resourcesList: [],

  entitiesGroups: [],
  unassignedShifts: [],

  flushInitiated: () => {
    set({
      initiated: false,
      assignmentsInitiated: false,
      resourcesInitiated: false,
    });
  },

  setSelectedShift: (shift: Shift | null) => {
    set({ selectedShift: shift });
  },
  loadCalendarData: async (startDate: Date, endDate: Date, timezone: string, filters = {}, sortingModel) => {
    set({ loading: true, assignmentsLoading: true, calendarDataLoading: true });
    const applyFilters = { ...filters };
    // add filter for Deleted status, if it's not already there
    if (!applyFilters.statuses) {
      applyFilters.statuses = {
        key: 'status',
        value: [
          SHIFT_STATUSES.ASSIGNED,
          SHIFT_STATUSES.COMPLETED,
          ...(applyFilters.hasAssignee === undefined ? [SHIFT_STATUSES.CANCELED] : []),
          // SHIFT_STATUSES.DELETED,
          SHIFT_STATUSES.EXPIRED,
          SHIFT_STATUSES.IN_PROGRESS,
          SHIFT_STATUSES.OPENED,
          SHIFT_STATUSES.PENDING,
          SHIFT_STATUSES.REASSIGNED,
          SHIFT_STATUSES.UNASSIGNED,
          SHIFT_STATUSES.UNFILLED,
        ],
      };
    }

    // Need to add 'All' staffing type to the filter if filter is not empty
    // Because any staffing type could fill shift with staffing type marked as `All`
    if (applyFilters.staffingtypes) {
      applyFilters.staffingtypes = {
        key: 'staffingtypes',
        value: ['All', ...(applyFilters.staffingtypes.value as string[])],
      };
    }

    if (!applyFilters.staffingStatuses) {
      applyFilters.staffingStatuses = {
        key: 'staffingStatuses',
        value: ['Active'],
      };
    }

    const filterByStaff = applyFilters.staff;

    try {
      const events = await getEvents({
        timezone,
        startDate: dayjs(startDate),
        endDate: dayjs(endDate),
        filters: {
          ...applyFilters,
          // We want to see all unassigned shifts when filter by staff is used, so we remove staff filter
          staff: { key: 'staff', value: [] },
        },
      });
      const assignments = await getAssignments(startDate.toISOString(), endDate.toISOString(), timezone);
      const talents = await getAllTalents({
        filters: applyFilters,
        orderBy: sortingModel || [],
      });
      const resourcesList = talents.filter((t) =>
        filterByStaff && Array.isArray(filterByStaff.value) ? filterByStaff.value.includes(t.id) : true,
      );

      let resources = resourcesList.map((resource) => {
        const shifts = assignments.data.reduce<Shift[]>((acc, assignment) => {
          if (assignment.staffId === resource.id) {
            const shift = events.find((e) => e.id === assignment.shiftId);
            shift && acc.push(shift as Shift);
          }
          return acc;
        }, []);
        return {
          resourceInfo: resource || ({} as StaffListItem),
          assignments: shifts,
        };
      });

      if (applyFilters.showScheduled?.value === 'yes') {
        resources = resources.filter((r) => r.assignments.length > 0);
      }

      const groups = Object.values(
        resources.reduce<
          Record<
            string,
            {
              groupId: string;
              groupName: string;
              items: {
                resourceInfo: StaffListItem;
                assignments: Shift[];
              }[];
            }
          >
        >((acc, row) => {
          const groupId = row.resourceInfo.agency?.id || 'internal';
          const groupName = row.resourceInfo.agency?.name || 'Internal';
          if (!acc[groupId]) {
            acc[groupId] = { groupId, groupName, items: [] };
          }
          acc[groupId].items.push(row);
          return acc;
        }, {}),
      );

      const index = groups.findIndex((group) => group.groupId === 'internal');

      const sortedGroups = index ? [groups[index], ...groups.slice(0, index), ...groups.slice(index + 1)] : groups;

      const unassigned = events.filter((event) => {
        return !assignments.data.find((assignment) => assignment.shiftId === event.id);
      });

      set({
        loading: false,
        eventsList: events,
        assignmentsLoading: false,
        assignmentsList: assignments.data,
        // TODO: currently the only available method to filter by staff is is to filter on frontend
        resourcesList,
        unassignedShifts: unassigned,
        entitiesGroups: sortedGroups,
        resourcesListLoading: false,
        calendarDataLoading: false,
        selectedShift: null,
      });
    } finally {
      set({ loading: false, initiated: true, assignmentsInitiated: true, resourcesInitiated: true });
    }
  },
}));
